import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  UserGroupIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  EyeIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

const SupplierInvitationPromptModal = ({ 
  isOpen, 
  onClose, 
  task,
  project,
  onInviteSuppliers,
  onViewTask 
}) => {
  const [relevantSuppliers, setRelevantSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && task) {
      loadRelevantSuppliers();
    }
  }, [isOpen, task]);

  const loadRelevantSuppliers = async () => {
    try {
      setLoading(true);
      
      // Get all suppliers and filter by category
      const suppliersSnapshot = await getDocs(collection(db, 'suppliers'));
      const allSuppliers = suppliersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter suppliers that match the task category
      const relevant = allSuppliers.filter(supplier => {
        const supplierCategory = supplier.category?.toLowerCase();
        const taskCategory = task.category?.toLowerCase();
        
        // Match exact category or if supplier specialties include the task category
        return supplierCategory === taskCategory || 
               supplier.specialties?.some(s => s.toLowerCase().includes(taskCategory));
      });

      setRelevantSuppliers(relevant.slice(0, 3)); // Show top 3 relevant suppliers
    } catch (error) {
      console.error('Error loading relevant suppliers:', error);
      setRelevantSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !task) return null;

  const handleInviteSuppliers = () => {
    if (onInviteSuppliers) {
      onInviteSuppliers(task);
    }
    onClose();
  };

  const handleViewTask = () => {
    if (onViewTask) {
      onViewTask(task);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="bg-blue-100 p-2 rounded-full mr-3">
                <ClipboardDocumentListIcon className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Task Created!</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-6">
            <p className="text-gray-600 mb-2">
              Your task <strong>"{task.name}"</strong> has been created successfully.
            </p>
            
            {task.category && (
              <div className="mb-4">
                <p className="text-gray-500 text-sm mb-2">
                  Since this is a <strong>{task.category}</strong> task, would you like to invite suppliers to quote?
                </p>
                
                {loading ? (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                    </div>
                  </div>
                ) : relevantSuppliers.length > 0 ? (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-xs font-medium text-blue-800 mb-2">
                      {relevantSuppliers.length} relevant suppliers available:
                    </p>
                    <div className="space-y-1">
                      {relevantSuppliers.map((supplier) => (
                        <div key={supplier.id} className="flex items-center text-xs text-blue-700">
                          <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                          {supplier.name}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">
                      No specific suppliers found for {task.category}, but you can still browse all suppliers.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <button
              onClick={handleInviteSuppliers}
              className="w-full flex items-center justify-center px-4 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors font-medium"
            >
              <PaperAirplaneIcon className="w-5 h-5 mr-2" />
              Yes, Invite Suppliers to Quote
            </button>
            
            <button
              onClick={handleViewTask}
              className="w-full flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              <EyeIcon className="w-5 h-5 mr-2" />
              Just View the Task
            </button>
            
            <button
              onClick={onClose}
              className="w-full flex items-center justify-center px-4 py-3 text-gray-500 hover:text-gray-700 transition-colors text-sm"
            >
              I'll invite suppliers later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierInvitationPromptModal;